import { http } from "@/lib/request";
import type {
  CityItem,
  PageParams,
  DecoratePagePayload,
  DecoratePageResult,
  LoginAccountPayload,
  LoginAccountResult,
  CouponPackagePopup,
} from "@/types/api";

export interface HomeServiceItem {
  id: number;
  shop_id?: number;
  name?: string;
  image?: string;
  price?: number;
  scribing_price?: number;
  duration?: number;
  order_num?: number;
  marketing_tags?: string[];
  tags?: string | string[];
}

export interface HomeServiceListResult {
  lists: HomeServiceItem[];
  count: number;
  page_no: number;
  page_size: number;
}

export interface WechatCodeUrlResult {
  url: string;
}

export interface WechatJsConfigResult {
  appId: string;
  timestamp: string | number;
  nonceStr: string;
  signature: string;
}

export interface OpenConfigResult {
  shop_name?: string;
  shop_logo?: string;
  domain?: string;
  tencent_map_key?: string;
}

export interface AgreementConfigResult {
  client?: string;
  service_title?: string;
  service_content?: string;
  privacy_title?: string;
  privacy_content?: string;
  payment_title?: string;
  payment_content?: string;
  user?: string;
  privacy?: string;
}

export interface ReverseGeocodeResult {
  status?: number;
  message?: string;
  result?: {
    address?: string;
    formatted_addresses?: {
      recommend?: string;
    };
    address_component?: {
      province?: string;
      city?: string;
      district?: string;
    };
  };
}

export interface NearbyLocationResult {
  status?: number;
  message?: string;
  data?: Array<{
    id?: string | number;
    title?: string;
    address?: string;
    _distance?: number;
    location?: {
      lat?: number | string;
      lng?: number | string;
    };
  }>;
}

export interface NearbyCityResult {
  opened?: boolean;
  current_city_name?: string;
  currentCityName?: string;
  raw_city_name?: string;
  raw_district_name?: string;
  location_adcode?: string;
  location_accuracy?: number | string | null;
  city_id?: number | null;
  cityId?: number | null;
  id?: number | null;
  name?: string;
}

export interface ToggleCollectResult {
  is_collect?: number;
  favorites_delta?: number;
}

export const appApi = {
  loginAccount(payload: LoginAccountPayload) {
    return http.post<LoginAccountResult>("/login/account", payload, {
      withAuth: false,
    });
  },

  getUserInfo() {
    return http.get<Record<string, unknown>>("/user/info");
  },

  sendSmsCode(payload: { mobile: string; type?: number }) {
    return http.post<void>("/sms/sendCode", {
      mobile: payload.mobile,
      type: payload.type ?? 1,
    }, {
      withAuth: false,
    });
  },

  bindMobile(payload: { mobile: string; captcha: string }) {
    return http.post<LoginAccountResult>("/user/bindMobile", payload);
  },

  getDecoratePage(params: DecoratePagePayload) {
    return http.get<DecoratePageResult>("/decorate/page", { params });
  },

  getHomeData() {
    return http.get<Record<string, unknown>>("/index");
  },

  getOpenConfig() {
    return http.get<OpenConfigResult>("/config/config", {
      withAuth: false,
    });
  },

  getAgreementConfig(client = "customer") {
    return http.get<AgreementConfigResult>("/config/agreement", {
      params: { client },
      withAuth: false,
    });
  },

  getGeocoderCoordinate(params: { location: string }) {
    return http.get<ReverseGeocodeResult>("/index/geocoderCoordinate", {
      params,
      withAuth: false,
    });
  },

  getNearbyLocation(params: { latitude: number; longitude: number; keyword?: string }) {
    return http.get<NearbyLocationResult>("/index/getNearbyLocation", {
      params,
      withAuth: false,
    });
  },

  getNearbyCity(params: { latitude: number; longitude: number; accuracy?: number }) {
    return http.get<NearbyCityResult>("/index/getNearbyCity", {
      params,
      withAuth: false,
    });
  },

  getCityList() {
    return http.get<CityItem[]>("/region/city", {
      withAuth: false,
    });
  },

  getHomeServiceList(params?: PageParams) {
    return http.get<HomeServiceListResult>("/index/serverLists", { params });
  },

  toggleCollect(payload: { id: number; type: number }) {
    return http.post<ToggleCollectResult>("/index/collect", payload);
  },

  getWechatCodeUrl(params: { url: string; scope?: string; state?: string }) {
    return http.get<WechatCodeUrlResult>("/login/codeUrl", {
      params,
      withAuth: false,
    });
  },

  getWechatJsConfig(params: { url: string }) {
    return http.get<WechatJsConfigResult>("/wechat/jsConfig", {
      params,
      withAuth: false,
    });
  },

  oaLogin(payload: { code: string }) {
    return http.post<LoginAccountResult>("/login/oaLogin", payload, {
      withAuth: false,
    });
  },

  getPendingCouponPackagePopup() {
    return http.get<CouponPackagePopup | null>("/coupon/package/pendingPopup");
  },

  markCouponPackagePopupRead(recordId: number) {
    return http.post<void>("/coupon/package/readPopup", { record_id: recordId });
  },
};
